/**
 * VendorsListPage — danh sách Vendor + tìm kiếm + filter type + CRUD.
 * Pagination server-side bind với API page/page_size.
 */
import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'

import DataTableMUI from '@/components/DataTableMUI'
import PageDataState from '@/components/PageDataState'
import ConfirmDialog from '@/components/ConfirmDialog'
import { usePagePermission } from '../../hooks/usePagePermission'
import VendorFormDialog from './VendorFormDialog'
import { useDeleteVendorMutation, useListVendorsQuery } from '@/store/api/vendorsApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { Vendor, VendorType } from '@/types/api'

const TYPE_OPTIONS: Array<{ value: '' | VendorType; label: string }> = [
  { value: '', label: 'All' },
  { value: 0, label: 'Customer' },
  { value: 1, label: 'Vendor' },
  { value: 2, label: 'Both' },
]

export function VendorsListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [typeFilter, setTypeFilter] = useState<'' | VendorType>('')
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })

  const [editing, setEditing] = useState<Vendor | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Vendor | null>(null)

  const { data, isLoading, error, isFetching } = useListVendorsQuery({
    q: debouncedSearch || undefined,
    type: typeFilter === '' ? undefined : typeFilter,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [deleteVendor, { isLoading: deleting }] = useDeleteVendorMutation()

  const rows = useMemo(() => (data?.items ?? []).map((v) => ({ id: v.code, ...v })), [data])

  const columns = useMemo<GridColDef<Vendor & { id: string }>[]>(
    () => [
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 220 },
      { field: 'short_name', headerName: 'Short Name', width: 160 },
      { field: 'phone', headerName: 'Phone', width: 140 },
      { field: 'email', headerName: 'Email', width: 200 },
      {
        field: 'type',
        headerName: 'Type',
        width: 110,
        valueGetter: (_v, row) => {
          if (row.type === 0) return 'Customer'
          if (row.type === 1) return 'Vendor'
          if (row.type === 2) return 'Both'
          return ''
        },
      },
      { field: 'vat_code', headerName: 'VAT Code', width: 140 },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                disabled={!canEdit}
                onClick={(e) => {
                  e.stopPropagation()
                  setEditing(params.row)
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                disabled={!canEdit}
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmDelete(params.row)
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteVendor(confirmDelete.code).unwrap()
      snack.success(`Deleted vendor ${confirmDelete.code}`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete vendor'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>
          Vendors
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setCreateOpen(true)}
        >
          New Vendor
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            label="Search"
            placeholder="Code, name, VAT..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPagination((p) => ({ ...p, page: 0 }))
            }}
            sx={{ minWidth: 260 }}
          />
          <TextField
            label="Type"
            select
            size="small"
            value={typeFilter}
            onChange={(e) => {
              const v = e.target.value
              setTypeFilter(v === '' ? '' : (Number(v) as VendorType))
              setPagination((p) => ({ ...p, page: 0 }))
            }}
            sx={{ minWidth: 160 }}
          >
            {TYPE_OPTIONS.map((opt) => (
              <MenuItem key={String(opt.value)} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper sx={{ p: 0 }}>
          <DataTableMUI
            rows={rows}
            columns={columns as GridColDef[]}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as Vendor)}
          />
        </Paper>
      </PageDataState>

      <VendorFormDialog
        open={createOpen || !!editing}
        mode={editing ? 'edit' : 'create'}
        initialValue={editing}
        onClose={() => {
          setCreateOpen(false)
          setEditing(null)
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete vendor"
        message={
          confirmDelete
            ? `Are you sure you want to delete vendor "${confirmDelete.code} — ${confirmDelete.name}"?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default VendorsListPage
